import React, { useRef, useEffect, useCallback, useState } from 'react';

import { Flex, Text, InputProps, Textarea } from '@chakra-ui/core';
import { Editor } from '@tinymce/tinymce-react';
import { useField } from '@unform/core';

import InputBadgeError from '../InputBadgeError';

interface IProps extends InputProps<HTMLTextAreaElement> {
  name: string;
  label?: string;
  isRequired?: boolean;
  formatChars?: {
    [key: string]: string;
  };
  badgeErrorColor?: string;
  mb?: string | number;
  onChange?: (data: any) => void
}

const TextEditor: React.FC<IProps> = ({
    name,
    label,
    isRequired = false,
    badgeErrorColor = 'red',
    defaultValue = '',
    onChange = null,
    width = '100%'
}) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [value, setValue] = useState('');

    const { fieldName, error, registerField } = useField(name);

    const handleChange = useCallback(newValue => {
        if(onChange) {
            onChange(newValue.level.content);
        }
        setValue(newValue.level.content);
    }, []);

    useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue as string);
        }

        registerField({
            name: fieldName,
            ref: inputRef.current,
            path: 'value'
        });
    }, [defaultValue, fieldName, registerField]);

    return (
        <Flex className="no-print" width={width} flexDirection="column" fontWeight="500" mb="64px" zIndex={20}>
            {label && (
                <Flex width="100%" textAlign="center" mb="4px">
                    <Text>{label}</Text>
                    {isRequired && (
                        <Text ml="8px" color="red.500">
              *
                        </Text>
                    )}
                </Flex>
            )}

            <Flex width="100%" flexDirection="column">
                <Editor
                    textareaName={name}
                    apiKey="9qa5qykx07kskrltsx8g7whfx6lb9ntfsbilaad579902xo9"
                    onInit={(evt, editor: any) => null}
                    initialValue={(defaultValue as any) || ''}
                    init={{
                        height: 500,
                        menubar: true,
                        plugins: [
                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'print', 'preview', 'archor',
                            'searchreplace', 'visualblocks', 'code', 'fullscreen',
                            'insertdatetime', 'media', 'table', 'paste', 'code', 'help', 'wordcount'
                        ],
                        toolbar:
              'undo redo | fontselect' +
              'bold italic backcolor | alignleft aligncenter ' +
              'alignright alignjustify | bullist numlist outdent indent | table link image | ' +
              'code code-block removeformat | help',
                        content_style:
              'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                        images_upload_url:
              'https://app.eflorista.com.br/api/v1/pages/upload',

                        image_uploadtab: true,
                        font_family_formats: 'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Oswald=oswald; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats'
                    }}
                    onChange={handleChange}
                />
                <Textarea ref={inputRef} display="none" name={name} value={value} />
                {error && <InputBadgeError error={error} color={badgeErrorColor} />}
            </Flex>
        </Flex>
    );
};

export default TextEditor;
